import { useCallback, useEffect, useRef, useState } from "react";
import apiClient from "@/api/client";
import { onRealtimeConnected } from "../api/realtime";
import {
  fetchVapidPublicKey,
  getExistingSubscription,
  getServiceWorkerRegistration,
  isWebPushSupported,
  subscribeWebPush,
  unsubscribeWebPush,
} from "../api/webPush";

// How often to proactively re-sync the subscription while the app stays open.
// Browsers rotate/expire push subscriptions silently and the backend prunes an
// endpoint the moment a send returns 410, so a tab left focused all day would
// otherwise never re-register its rotated endpoint until a reload/refocus.
const RESYNC_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6 h

export type WebPushPermission = NotificationPermission | "unsupported";

export interface WebPushState {
  /** Browser supports push AND the backend has VAPID configured. */
  available: boolean;
  subscribed: boolean;
  /** Live browser permission state, kept in sync with the OS/browser. */
  permission: WebPushPermission;
  /** True once availability + subscription status have been checked. */
  ready: boolean;
  busy: boolean;
  error: string | null;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
  clearError: () => void;
}

function currentPermission(): WebPushPermission {
  return typeof Notification !== "undefined" ? Notification.permission : "unsupported";
}

// Records whether the user actually wants push on this device. Disabling push
// does NOT revoke the browser permission (it stays "granted"), so without this
// flag the self-healing sync below would mistake a user-initiated unsubscribe
// for an expired subscription and silently turn notifications back on.
const OPT_IN_KEY = "gg-push-opt-in";

function isOptedIn(): boolean {
  try {
    return localStorage.getItem(OPT_IN_KEY) === "1";
  } catch {
    return false;
  }
}

function setOptedIn(value: boolean): void {
  try {
    if (value) localStorage.setItem(OPT_IN_KEY, "1");
    else localStorage.removeItem(OPT_IN_KEY);
  } catch {
    // ignore storage errors
  }
}

export function useWebPush(): WebPushState {
  const [available, setAvailable] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [permission, setPermission] = useState<WebPushPermission>(currentPermission);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Read inside event listeners that are registered once, so they always see
  // the latest availability without re-binding.
  const availableRef = useRef(false);

  // Reconcile React state with the *actual* browser state. Self-healing:
  // when permission is granted but the browser subscription has expired/rotated
  // (which silently disables push after a while), it recreates it and re-syncs
  // the backend. Safe to call repeatedly — re-subscribing while already granted
  // never shows a permission prompt.
  const sync = useCallback(async () => {
    const perm = currentPermission();
    setPermission(perm);
    if (!availableRef.current) return;
    if (perm !== "granted") {
      setSubscribed(false);
      return;
    }
    try {
      let sub = await getExistingSubscription();
      if (!sub) {
        // Permission is granted but there is no subscription. Only self-heal
        // (recreate) when the user actually opted in on this device — otherwise
        // this is a user-initiated disable and must stay off across refreshes.
        if (!isOptedIn()) {
          setSubscribed(false);
          return;
        }
        await subscribeWebPush();
        sub = await getExistingSubscription();
        setSubscribed(!!sub);
        return;
      }
      // A live subscription exists — make sure the opt-in flag reflects reality
      // (covers users who enabled before this flag existed).
      setOptedIn(true);
      setSubscribed(true);
      // Re-register SW so browsers pick up sw.js updates promptly.
      void getServiceWorkerRegistration();
      // Re-send subscription to backend — ensures the record exists even if the
      // backend lost it (DB reset, pruned as stale, new environment, etc.).
      void apiClient.post("/realtime/webpush/subscriptions/", sub.toJSON()).catch(() => {});
    } catch {
      // Network/SW hiccup — leave current state, next sync retries.
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!isWebPushSupported()) {
      setPermission("unsupported");
      setReady(true);
      return;
    }
    (async () => {
      try {
        const key = await fetchVapidPublicKey();
        if (cancelled || !key) {
          setReady(true);
          return;
        }
        setAvailable(true);
        availableRef.current = true;
        await sync();
      } catch {
        // Backend unreachable or key endpoint failing — keep the toggle hidden.
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sync]);

  // Keep state synced with reality: when the tab regains focus, and whenever the
  // browser's notification permission changes (e.g. revoked in site settings or
  // granted in another tab). This is what makes the UI reflect the *current*
  // status instead of a stale snapshot from mount.
  useEffect(() => {
    if (!available) return;

    const onVisible = () => {
      if (document.visibilityState === "visible") void sync();
    };
    document.addEventListener("visibilitychange", onVisible);

    let permStatus: PermissionStatus | null = null;
    const onPermChange = () => void sync();
    if (navigator.permissions?.query) {
      navigator.permissions
        .query({ name: "notifications" as PermissionName })
        .then((status) => {
          permStatus = status;
          status.addEventListener("change", onPermChange);
        })
        .catch(() => {});
    }

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      permStatus?.removeEventListener("change", onPermChange);
    };
  }, [available, sync]);

  // Proactive re-sync so a rotated/expired subscription is re-registered on the
  // backend BEFORE the next push fails with 410 and the row is pruned. The
  // existing mount/visibility/permission triggers only fire opportunistically;
  // they miss a tab kept open and focused for hours. Re-sync on a timer and on
  // every realtime reconnect (a good proxy for "the device just woke / came
  // back online"). `sync()` is idempotent — re-posting an unchanged endpoint is
  // a harmless upsert.
  useEffect(() => {
    if (!available) return;
    const timer = window.setInterval(() => void sync(), RESYNC_INTERVAL_MS);
    const offConnected = onRealtimeConnected(() => void sync());
    return () => {
      window.clearInterval(timer);
      offConnected();
    };
  }, [available, sync]);

  const enable = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await subscribeWebPush();
      setOptedIn(true);
      setSubscribed(true);
      setPermission(currentPermission());
    } catch (e) {
      setSubscribed(false);
      setPermission(currentPermission());
      setError(e instanceof Error ? e.message : "Nie udało się włączyć powiadomień.");
    } finally {
      setBusy(false);
    }
  }, []);

  const disable = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      // Clear opt-in first so a concurrent sync (e.g. tab refocus) can't
      // re-create the subscription we are about to remove.
      setOptedIn(false);
      await unsubscribeWebPush();
      setSubscribed(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Nie udało się wyłączyć powiadomień.");
    } finally {
      setBusy(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    available,
    subscribed,
    permission,
    ready,
    busy,
    error,
    enable,
    disable,
    clearError,
  };
}
